import { Modal, Table, Spin, message, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { waypointService, type WaypointDetail } from '../../../services/api/waypointService';

const { Text } = Typography;

interface WaypointDetailModalProps {
  visible: boolean;
  onClose: () => void;
  waypointId: number | null;
}

export const WaypointDetailModal: React.FC<WaypointDetailModalProps> = ({
  visible,
  onClose,
  waypointId,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [waypoint, setWaypoint] = useState<WaypointDetail | null>(null);

  useEffect(() => {
    if (visible && waypointId) {
      loadWaypointDetail();
    }
  }, [visible, waypointId]);

  const loadWaypointDetail = async () => {
    if (!waypointId) return;

    setLoading(true);
    try {
      const data = await waypointService.getWaypoint(waypointId);
      setWaypoint(data);
    } catch (error: any) {
      console.error('Error loading waypoint detail:', error);
      message.error('Error al cargar el detalle del waypoint');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t('waypoint.name') || 'Nombre',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
    },
    {
      title: t('waypoint.latitude') || 'Latitud',
      dataIndex: 'latitude',
      key: 'latitude',
      align: 'right' as const,
      render: (value: number) => value?.toFixed(6) || '-',
    },
    {
      title: t('waypoint.longitude') || 'Longitud',
      dataIndex: 'longitude',
      key: 'longitude',
      align: 'right' as const,
      render: (value: number) => value?.toFixed(6) || '-',
    },
    {
      title: t('waypoint.date_time') || 'Fecha/Hora',
      dataIndex: 'date_taken',
      key: 'date_taken',
      render: (value: string) => {
        if (!value) return '-';
        try {
          return new Date(value).toLocaleString();
        } catch {
          return value;
        }
      },
    },
  ];

  return (
    <Modal
      title={waypoint ? `${t('waypoint.title')} - ${waypoint.auditor.name} (${waypoint.waypoint_date})` : t('waypoint.detail_title')}
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : waypoint ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <Text strong>{t('waypoint.total_waypoints')}: </Text>
            <Text>{waypoint.waypoints.length}</Text>
          </div>
          <Table
            dataSource={waypoint.waypoints}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t('common.total') || 'Total'}: ${total} ${t('waypoint.title').toLowerCase()}`,
            }}
            size="small"
          />
        </>
      ) : null}
    </Modal>
  );
};
